/**
 * THE recap addressing module — doc ids and public URLs, for backend AND frontend.
 *
 * Everything that names a recap goes through here. Two rules, one place:
 *
 *   1. DOC ID. Abstract keeps the historical `recap_<date>`; every other chain mints
 *      `recap_<chain>_<date>`. Abstract's ~110 published docs, the four
 *      `/api/content/recap/[recapId]/*` routes, every `tweet_queue` row keyed
 *      `ecosystem_recap:recap_<date>` and `reconcileOutboxStrays` all address Abstract's
 *      recaps by that exact id — migrating them is a four-surface break. Without the
 *      namespace on the other side, a second recap-ready chain either SILENTLY never
 *      generates (`getRecap(date)` finds Abstract's doc → `{skipped:true}`) or, under
 *      `forceRegenerate`, OVERWRITES Abstract's published recap (`storeRecap` is a `.set`).
 *
 *   2. PUBLIC URL. Abstract reads at the bare `/daily/<date>/`; every other chain reads at
 *      `/<hubSlug>/daily/<date>/`. The bare address is load-bearing: posted tweet cards, the
 *      RSS/Atom feeds, ~110 indexed pages and the `/api/ai/daily` citation format all point
 *      at it, so it can never become chain-required. The OG card URL follows the same shape —
 *      `&chain=` is appended for non-default chains ONLY, keeping every Abstract card URL
 *      already baked into a live tweet byte-identical.
 *
 * A recap doc's chain is read through `recapChainOf`: pre-multichain docs carry NO `chain`
 * field, and Firestore `==` cannot match a missing field, so absent ⇒ Abstract is applied
 * IN MEMORY everywhere. A query-side `where('chain','==','abstract')` returns zero of those
 * ~110 docs — that is the footgun this module exists to make un-writable.
 *
 * PURE + zero-require beyond the chain registry (itself zero-require), so gates, the MCP
 * server, backend services and Next server components can all share it.
 */
const {
  DEFAULT_CHAIN_ID, getChain, getChainLabel, recapReadyChainIds,
} = require('../config/chains.js');

/** The chain a recap belongs to when nothing says otherwise. */
const DEFAULT_RECAP_CHAIN = DEFAULT_CHAIN_ID;

/**
 * How many chains can interleave in ONE date-ordered `ecosystem_recaps` stream.
 *
 * Every chain scope over recaps is applied in memory (a query-side `chain ==` matches none of
 * the pre-multichain docs), so every such read has to OVER-FETCH by this factor before it
 * trims. Getting it wrong is silent in exactly the way this whole leg keeps being bitten by:
 * a flat `limit(12)` still returns 12 rows, just ~6 of THIS chain's, and a ten-recap sentiment
 * streak quietly stops resolving with no error anywhere.
 *
 * Derived from the live slate, never hard-coded, so chain #3 needs no code change. Floored at
 * 1 so an empty registry can never produce `limit(0)`.
 */
function chainFanout() {
  return Math.max(1, recapReadyChainIds().length);
}

/**
 * A recap's effective chain. Accepts the raw field, a recap doc, or nothing.
 * Absent / empty / non-string ⇒ the default chain (a pre-multichain doc is Abstract's).
 */
function recapChainOf(chainOrDoc) {
  if (chainOrDoc && typeof chainOrDoc === 'object') {
    return typeof chainOrDoc.chain === 'string' && chainOrDoc.chain
      ? chainOrDoc.chain
      : DEFAULT_RECAP_CHAIN;
  }
  return typeof chainOrDoc === 'string' && chainOrDoc ? chainOrDoc : DEFAULT_RECAP_CHAIN;
}

/** Firestore doc id for one chain's recap on one date. See rule 1 above. */
function recapDocId(chain, dateString) {
  const id = recapChainOf(chain);
  return id === DEFAULT_RECAP_CHAIN ? `recap_${dateString}` : `recap_${id}_${dateString}`;
}

/**
 * Canonical reader PATH (leading slash, trailing slash). See rule 2 above.
 * An unknown chain id degrades to the bare Abstract path rather than minting a 404 hub route.
 */
function recapPath(dateString, chain) {
  const id = recapChainOf(chain);
  if (id === DEFAULT_RECAP_CHAIN) return `/daily/${dateString}/`;
  const meta = getChain(id);
  return meta && meta.hubSlug ? `/${meta.hubSlug}/daily/${dateString}/` : `/daily/${dateString}/`;
}

/** Absolute reader URL. `siteUrl` may carry a trailing slash. */
function recapUrl(siteUrl, dateString, chain) {
  return `${String(siteUrl || '').replace(/\/$/, '')}${recapPath(dateString, chain)}`;
}

/** OG card PATH. `&chain=` for non-default chains only — Abstract stays byte-identical. */
function recapOgCardPath(dateString, chain) {
  const id = recapChainOf(chain);
  return id === DEFAULT_RECAP_CHAIN
    ? `/api/og/daily?date=${dateString}`
    : `/api/og/daily?date=${dateString}&chain=${id}`;
}

/** Absolute OG card URL (the tweet's `imageUrl`). */
function recapOgCardUrl(siteUrl, dateString, chain) {
  return `${String(siteUrl || '').replace(/\/$/, '')}${recapOgCardPath(dateString, chain)}`;
}

/**
 * Display title for a recap that has none — "<Chain> Daily Recap — <date>".
 * Chain-derived so a non-Abstract fallback never says "Abstract".
 */
function recapFallbackTitle(dateString, chain) {
  const id = recapChainOf(chain);
  return `${getChainLabel(id) || getChainLabel(DEFAULT_RECAP_CHAIN)} Daily Recap — ${dateString}`;
}

module.exports = {
  DEFAULT_RECAP_CHAIN,
  chainFanout,
  recapChainOf,
  recapDocId,
  recapPath,
  recapUrl,
  recapOgCardPath,
  recapOgCardUrl,
  recapFallbackTitle,
};
